import type { QuietWindow } from '../types/domain.types';

import { MINUTE_MS } from './istClock';

const MINUTES_PER_DAY = 24 * 60;

export type ParsedMinute =
  | { readonly ok: true; readonly value: number }
  | { readonly ok: false; readonly reason: 'EMPTY' | 'MALFORMED' | 'OUT_OF_RANGE' };

const HHMM = /^(\d{2}):(\d{2})$/;

export const minuteToHhmm = (minute: number): string => {
  const hours = Math.floor(minute / 60);
  const minutes = minute % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const hhmmToMinute = (text: string): ParsedMinute => {
  const trimmed = text.trim();
  if (trimmed === '') return { ok: false, reason: 'EMPTY' };
  const match = HHMM.exec(trimmed);
  if (!match) return { ok: false, reason: 'MALFORMED' };
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return { ok: false, reason: 'OUT_OF_RANGE' };
  return { ok: true, value: hours * 60 + minutes };
};

export type QuietWindowProblem = 'EMPTY' | 'MALFORMED' | 'SAME';

export type QuietHoursProblem = 'NONE' | QuietWindowProblem;

export type FieldProblem = QuietWindowProblem | null;

export interface QuietWindowFailure {
  readonly ok: false;
  readonly problem: QuietWindowProblem;
  readonly from: FieldProblem;
  readonly to: FieldProblem;
}

export type QuietWindowValidation = { readonly ok: true; readonly window: QuietWindow } | QuietWindowFailure;

const faultOf = (parsed: ParsedMinute): FieldProblem => {
  if (parsed.ok) return null;
  return parsed.reason === 'EMPTY' ? 'EMPTY' : 'MALFORMED';
};

export const validateQuietWindow = (from: string, to: string): QuietWindowValidation => {
  const start = hhmmToMinute(from);
  const end = hhmmToMinute(to);
  if (!start.ok || !end.ok) {
    const onFrom = faultOf(start);
    const onTo = faultOf(end);
    return { ok: false, problem: onFrom === 'EMPTY' || onTo === 'EMPTY' ? 'EMPTY' : 'MALFORMED', from: onFrom, to: onTo };
  }
  if (start.value === end.value) return { ok: false, problem: 'SAME', from: 'SAME', to: 'SAME' };
  return { ok: true, window: { startMinute: start.value, endMinute: end.value } };
};

export const sameWindow = (a: QuietWindow, b: QuietWindow): boolean =>
  a.startMinute === b.startMinute && a.endMinute === b.endMinute;

export type QuietWindowChange = { readonly ok: true; readonly window: QuietWindow | null } | QuietWindowFailure;

export const quietWindowChange = (saved: QuietWindow, from: string, to: string): QuietWindowChange => {
  const checked = validateQuietWindow(from, to);
  if (!checked.ok) return checked;
  return { ok: true, window: sameWindow(checked.window, saved) ? null : checked.window };
};

const CLOCK_24H: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'UTC' };
const CLOCKS = new Map<string, Intl.DateTimeFormat>();

const clockFor = (locale: string): Intl.DateTimeFormat => {
  const hit = CLOCKS.get(locale);
  if (hit) return hit;
  let made: Intl.DateTimeFormat;
  try {
    made = new Intl.DateTimeFormat(locale, CLOCK_24H);
  } catch {
    made = new Intl.DateTimeFormat('en', CLOCK_24H);
  }
  CLOCKS.set(locale, made);
  return made;
};

export const quietClock = (minute: number, locale = 'en'): string => clockFor(locale).format(minute * MINUTE_MS);

export const formatWindow = (window: QuietWindow, locale = 'en'): string =>
  `${quietClock(window.startMinute, locale)}–${quietClock(window.endMinute, locale)}`;

export interface WindowSummary {
  readonly overnight: boolean;
  readonly minutes: number;
}

export const windowSummary = (window: QuietWindow): WindowSummary => {
  const overnight = window.endMinute < window.startMinute;
  const minutes = overnight ? MINUTES_PER_DAY - window.startMinute + window.endMinute : window.endMinute - window.startMinute;
  return { overnight, minutes };
};

export interface QuietWindowTouched {
  readonly from: boolean;
  readonly to: boolean;
}

export type QuietWindowField = keyof QuietWindowTouched;

export const UNTOUCHED: QuietWindowTouched = { from: false, to: false };

const problemOf = (from: FieldProblem, to: FieldProblem): QuietWindowProblem => {
  if (from === 'EMPTY' || to === 'EMPTY') return 'EMPTY';
  if (from === 'SAME' || to === 'SAME') return 'SAME';
  return 'MALFORMED';
};

export const visibleFailure = (
  failure: QuietWindowFailure | null,
  touched: QuietWindowTouched,
  submitted: boolean,
): QuietWindowFailure | null => {
  if (failure === null) return null;
  if (submitted) return failure;
  const from = touched.from ? failure.from : null;
  const to = touched.to ? failure.to : null;
  if (from === null && to === null) return null;
  if (from === failure.from && to === failure.to) return failure;
  return { ok: false, problem: problemOf(from, to), from, to };
};

export interface QuietHoursInput {
  readonly enabled: boolean;
  readonly from: string;
  readonly to: string;
}

export type QuietHoursPatchResult =
  | { readonly ok: true; readonly patch: { quietHoursEnabled?: boolean; quietHours?: QuietWindow } | null }
  | QuietWindowFailure;

export const quietHoursPatch = (
  server: { readonly quietHoursEnabled: boolean; readonly quietHours: QuietWindow },
  input: QuietHoursInput,
): QuietHoursPatchResult => {
  const toggle = input.enabled === server.quietHoursEnabled ? {} : { quietHoursEnabled: input.enabled };
  if (!input.enabled) return { ok: true, patch: Object.keys(toggle).length === 0 ? null : toggle };
  const change = quietWindowChange(server.quietHours, input.from, input.to);
  if (!change.ok) return change;
  const patch = change.window === null ? toggle : { ...toggle, quietHours: change.window };
  return { ok: true, patch: Object.keys(patch).length === 0 ? null : patch };
};
